// Resolving an amount to a scale (docs/adr/0006).
//
// A recipe declares two facts — the measured weight of the finished batch
// and how many portions it was divided into. Together they make three units
// interchangeable: 500 g of ingredients that cooked down to 424 g and was
// split in 5 means 1 porsjon ≡ 85 g ≡ 0.2 oppskrift.
//
// The finished weight is a denominator and nothing else. Water boiling off
// removes weight, not calories, and water stirred in adds weight without
// them — so it never touches the nutrition numbers.

import type {
  AmountUnit,
  ComponentAmount,
  Ingredient,
  Meal,
  Recipe,
  RecipeComponent,
} from "@/lib/schema";

const AMOUNT_UNITS: AmountUnit[] = ["grams", "recipe", "portions"];

export const AMOUNT_UNIT_LABELS: Record<AmountUnit, string> = {
  grams: "gram",
  recipe: "oppskrift",
  portions: "porsjon",
};

export function sumGrams(ingredients: Ingredient[]): number {
  return ingredients.reduce((sum, ing) => sum + ing.grams, 0);
}

// Absent ≡ the raw ingredient sum: recipes saved before finished weights
// existed, and anything the user never corrected, weigh what went into them.
export function recipeFinishedGrams(recipe: Recipe): number {
  const declared = recipe.finishedGrams;
  return typeof declared === "number" &&
    Number.isFinite(declared) &&
    declared > 0
    ? declared
    : sumGrams(recipe.ingredients);
}

export function recipePortions(recipe: Recipe): number {
  const declared = recipe.portions;
  return typeof declared === "number" &&
    Number.isFinite(declared) &&
    declared >= 1
    ? Math.floor(declared)
    : 1;
}

export function componentFinishedGrams(component: RecipeComponent): number {
  const copied = component.finishedGrams;
  return typeof copied === "number" && Number.isFinite(copied) && copied > 0
    ? copied
    : sumGrams(component.ingredients);
}

export function componentPortions(component: RecipeComponent): number {
  const copied = component.portions;
  return typeof copied === "number" && Number.isFinite(copied) && copied >= 1
    ? Math.floor(copied)
    : 1;
}

// Components stored before units existed carry a bare `portion`, which was
// always a whole-recipe multiplier.
export function componentAmount(component: RecipeComponent): ComponentAmount {
  const stored = component.amount;
  if (
    stored &&
    AMOUNT_UNITS.includes(stored.unit) &&
    Number.isFinite(stored.value) &&
    stored.value > 0
  ) {
    return stored;
  }
  const legacy = component.portion;
  return {
    unit: "recipe",
    value:
      typeof legacy === "number" && Number.isFinite(legacy) && legacy > 0
        ? legacy
        : 1,
  };
}

function amountToScale(
  amount: ComponentAmount,
  finishedGrams: number,
  portions: number,
): number {
  switch (amount.unit) {
    case "grams":
      return finishedGrams > 0 ? amount.value / finishedGrams : 0;
    case "portions":
      return portions > 0 ? amount.value / portions : 0;
    default:
      return amount.value;
  }
}

// The multiplier a component's reference-scale metrics get scaled by.
export function componentScale(component: RecipeComponent): number {
  return amountToScale(
    componentAmount(component),
    componentFinishedGrams(component),
    componentPortions(component),
  );
}

// The eaten weight off a kitchen scale, as opposed to the scaled sum of the
// raw ingredient rows. They differ exactly when the dish gained or lost
// weight while it was made.
export function componentEatenGrams(component: RecipeComponent): number {
  return componentFinishedGrams(component) * componentScale(component);
}

export function componentRawGrams(component: RecipeComponent): number {
  return sumGrams(component.ingredients) * componentScale(component);
}

// The chat component has no finished weight of its own, so its fraction is
// a bare number. `portion` is the field's former name.
export function mealScale(meal: Meal): number {
  const scale = meal.scale ?? meal.portion;
  return typeof scale === "number" && Number.isFinite(scale) && scale > 0
    ? scale
    : 1;
}

// «porsjon» is offered only once the batch is actually divided — at one
// portion it would resolve to exactly the same scale as «oppskrift».
export function unitsFor(portions: number): AmountUnit[] {
  return portions > 1 ? AMOUNT_UNITS : ["grams", "recipe"];
}
